import {
  collection,
  addDoc,
  doc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  increment,
} from 'firebase/firestore';
import { db } from './firebase';
import { GameAnswer, GameMode, Category, Difficulty, UserStats } from '../types';

/**
 * Firestore Collections:
 * - games: Stores individual game sessions
 * - users/{uid}/stats: Stores aggregated user statistics
 */

/**
 * Save a completed game to Firestore
 */
export const saveGameToFirestore = async (
  userId: string,
  gameData: {
    mode: GameMode;
    category: Category | 'all';
    difficulty: Difficulty | 'mixed';
    questions: string[]; // question IDs
    answers: GameAnswer[];
    score: number;
    accuracy: number;
    totalTime: number;
    longestStreak: number;
  }
): Promise<string> => {
  try {
    const gameDoc = {
      userId,
      mode: gameData.mode,
      category: gameData.category,
      difficulty: gameData.difficulty,
      questionIds: gameData.questions,
      answers: gameData.answers,
      score: gameData.score,
      accuracy: gameData.accuracy,
      totalTime: gameData.totalTime,
      longestStreak: gameData.longestStreak,
      completedAt: Timestamp.now(),
      createdAt: Timestamp.now(),
    };

    const docRef = await addDoc(collection(db, 'games'), gameDoc);

    // Update user stats
    await updateUserStats(userId, gameData);

    return docRef.id;
  } catch (error) {
    console.error('Error saving game to Firestore:', error);
    throw error;
  }
};

/**
 * Update user statistics after a game
 */
export const updateUserStats = async (
  userId: string,
  gameData: {
    score: number;
    accuracy: number;
    longestStreak: number;
    category: Category | 'all';
    answers: GameAnswer[];
  }
): Promise<void> => {
  try {
    console.log('🎮 Updating user stats for:', userId);
    console.log('📊 Game data:', gameData);

    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    const correctAnswers = gameData.answers.filter(a => a.isCorrect).length;
    const totalAnswers = gameData.answers.length;

    if (!userDoc.exists()) {
      console.error('❌ User document not found for:', userId);
      throw new Error('User document not found');
    }

    console.log('✅ User document found');

    const userData = userDoc.data();
    const currentStats = userData.stats || {
      gamesPlayed: 0,
      gamesWon: 0,
      totalScore: 0,
      averageScore: 0,
      bestScore: 0,
      currentStreak: 0,
      longestStreak: 0,
      favoriteCategory: null,
      accuracyRate: 0,
    };

    // Calculate new stats
    const newGamesPlayed = currentStats.gamesPlayed + 1;
    const newTotalScore = currentStats.totalScore + gameData.score;
    const newAverageScore = Math.round(newTotalScore / newGamesPlayed);
    const newBestScore = Math.max(currentStats.bestScore || 0, gameData.score);
    const newLongestStreak = Math.max(currentStats.longestStreak || 0, gameData.longestStreak);

    // Calculate total correct answers for overall accuracy
    const previousTotalCorrect = Math.round((currentStats.accuracyRate / 100) * (currentStats.gamesPlayed * totalAnswers));
    const newTotalCorrect = previousTotalCorrect + correctAnswers;
    const newTotalAnswers = (currentStats.gamesPlayed * totalAnswers) + totalAnswers;
    const newAccuracyRate = Math.round((newTotalCorrect / newTotalAnswers) * 100);

    // Calculate XP gain (10 XP per point scored)
    const xpGain = Math.round(gameData.score / 10);
    const newXP = (userData.xp || 0) + xpGain;

    // Calculate new rank based on XP
    let newRank = userData.rank || 'Seeker';
    if (newXP >= 50000) newRank = 'Master';
    else if (newXP >= 15000) newRank = 'Prophet';
    else if (newXP >= 5000) newRank = 'Scholar';
    else if (newXP >= 1000) newRank = 'Disciple';
    else newRank = 'Seeker';

    console.log('📈 New stats calculated:', {
      gamesPlayed: newGamesPlayed,
      totalScore: newTotalScore,
      averageScore: newAverageScore,
      bestScore: newBestScore,
      longestStreak: newLongestStreak,
      accuracyRate: newAccuracyRate,
      xpGain,
      newXP,
      newRank
    });

    // Update user document with new stats
    await updateDoc(userRef, {
      'stats.gamesPlayed': newGamesPlayed,
      'stats.totalScore': newTotalScore,
      'stats.averageScore': newAverageScore,
      'stats.bestScore': newBestScore,
      'stats.longestStreak': newLongestStreak,
      'stats.accuracyRate': newAccuracyRate,
      'stats.favoriteCategory': gameData.category === 'all' ? currentStats.favoriteCategory : gameData.category,
      xp: increment(xpGain),
      rank: newRank,
    });

    console.log('✅ Stats successfully updated in Firestore!');
  } catch (error) {
    console.error('❌ Error updating user stats:', error);
    throw error;
  }
};

/**
 * Get user statistics
 */
export const getUserStats = async (userId: string): Promise<UserStats | null> => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      return null;
    }

    const data = userDoc.data();
    return data.stats || {
      gamesPlayed: 0,
      gamesWon: 0,
      totalScore: 0,
      averageScore: 0,
      bestScore: 0,
      currentStreak: 0,
      longestStreak: 0,
      favoriteCategory: null,
      accuracyRate: 0,
    };
  } catch (error) {
    console.error('Error getting user stats:', error);
    return null;
  }
};

/**
 * Get recent games for a user
 */
export const getRecentGames = async (userId: string, limitCount: number = 10): Promise<any[]> => {
  try {
    const gamesRef = collection(db, 'games');
    const q = query(
      gamesRef,
      where('userId', '==', userId),
      orderBy('completedAt', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);
    const games: any[] = [];

    querySnapshot.forEach((doc) => {
      games.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    return games;
  } catch (error) {
    console.error('Error getting recent games:', error);
    return [];
  }
};

/**
 * Get leaderboard data
 */
export const getLeaderboard = async (limitCount: number = 50): Promise<any[]> => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(
      usersRef,
      orderBy('xp', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);
    const leaderboard: any[] = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      leaderboard.push({
        uid: doc.id,
        displayName: data.displayName,
        avatar: data.avatar,
        rank: data.rank,
        xp: data.xp,
        stats: data.stats,
      });
    });

    return leaderboard;
  } catch (error) {
    console.error('Error getting leaderboard:', error);
    return [];
  }
};
